import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingState, StatePanel, V2Button } from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { refreshSession } from '../../lib/auth.js';
import type { ShellContextResponse } from '../../lib/types/shell.js';
import { evaluateSalonAccess } from './salonAccess.ts';

// The salon bundle (UI, fonts, fictional dataset) is a separate chunk that is
// requested only AFTER the server has confirmed this tenant is entitled and
// the caller is authenticated — an anonymous visitor never downloads it.
const SalonSession = lazy(() => import('./SalonSession.js'));

/**
 * UI_REFERENCE_SOURCE: Claude Design export "sakhtar-salon" (beauty-salon
 * showcase prototype) — preserved as delivered; see docs/agents/handoffs/salon-showcase.md.
 *
 * `/t/:slug/salon/*` — a dedicated, standalone application shell (no ERP
 * sidebar, no finance/inventory navigation). Authentication stays the ERP's
 * own tenant session: this gate fetches the authenticated shell-context and
 * renders nothing salon-related unless the server says this tenant is
 * entitled. Same recovery contract as `AuthenticatedShell`: ADR-001 §2.3
 * makes an expired session answer 404, so a 404/401 first tries one session
 * refresh and then sends the visitor to the tenant login.
 */
export function SalonRoute() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [retryKey, setRetryKey] = useState(0);
  const recoveryAttempted = useRef(false);
  const base = slug ? tenantBasePath(slug) : '';
  const context = useApiGet<ShellContextResponse>(
    slug ? `${base}/api/v1/admin/shell-context` : null,
    [slug, retryKey],
  );

  const needsRecovery =
    context.status === 'error' &&
    (context.error.status === 404 || context.error.status === 401 || context.error.status === 403);
  const membershipDisabled =
    context.status === 'error' && context.error.code === 'MEMBERSHIP_DISABLED';

  useEffect(() => {
    if (!slug || !needsRecovery || membershipDisabled) return;
    if (recoveryAttempted.current) {
      navigate(`${base}/login`, { replace: true });
      return;
    }
    recoveryAttempted.current = true;
    refreshSession(slug)
      .then(() => setRetryKey((k) => k + 1))
      .catch(() => navigate(`${base}/login`, { replace: true }));
  }, [slug, needsRecovery, membershipDisabled, base, navigate]);

  if (!slug) return null;

  if (context.status === 'loading' || needsRecovery) return <LoadingState />;

  if (context.status === 'error') {
    return (
      <Centered>
        <StatePanel
          variant="network_error"
          title="ارتباط با سرور برقرار نشد"
          description="لطفاً دوباره تلاش کنید."
          action={
            <V2Button variant="primary" onClick={context.reload}>
              تلاش دوباره
            </V2Button>
          }
        />
      </Centered>
    );
  }

  const access = evaluateSalonAccess(context.data);
  if (access.kind === 'denied') {
    return (
      <Centered>
        <StatePanel
          variant="not_found"
          title="این برنامه برای فضای کاری شما فعال نیست"
          description="دسترسی به برنامه نمایشی سالن برای این سازمان تعریف نشده است."
          action={
            <V2Button variant="primary" onClick={() => navigate(`${base}/admin`)}>
              بازگشت به خانه
            </V2Button>
          }
        />
      </Centered>
    );
  }

  return (
    <Suspense fallback={<LoadingState />}>
      <SalonSession slug={slug} principalId={access.principalId} />
    </Suspense>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      dir="rtl"
      style={{
        height: '100dvh',
        display: 'grid',
        placeContent: 'center',
        background: 'var(--sk-bg-canvas)',
      }}
    >
      {children}
    </div>
  );
}
